import { useOrganization } from '@/contexts/useOrganization'
import type { IdpConnection } from '@/generated/http-clients/v3ApiSchemas'
import {
  Button,
  CloseButton,
  Dialog,
  Skeleton,
  Switch,
  Table,
  Tag,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { strategyDisplayNameMap } from '../utils/idpUtils'

export const OrgFeaturesSsoConnections = () => {
  const { t } = useTranslation('features')
  const { idp } = useOrganization()
  const idpConnections = idp.idpConnections

  const noIdpConnection =
    idpConnections.isSuccess && idpConnections?.data?.items?.length === 0

  const showIdpConnectionList =
    idpConnections.isSuccess && idpConnections?.data?.items?.length > 0

  const atLeastOneActiveConnection =
    idpConnections.isSuccess &&
    idpConnections?.data?.items?.some((conn) => conn.enabled)

  const { open, onOpen, onClose } = useDisclosure()
  const [modalContent, setModalContent] = useState({
    title: '',
    message: [''],
    onConfirm: () => void 0,
  })

  function onToggleConnection(connection: IdpConnection, enabled: boolean) {
    // if there's no active connection, we can directly update the state
    // without displaying a modal
    if (!atLeastOneActiveConnection) {
      idp.updateOrgIdpConnection(connection.id, enabled)
      return
    }

    // otherwise ask for confirmation before enabling/disabling
    const key = enabled ? 'enable' : 'disable'
    setModalContent({
      title: t(`organization.features.sso.modals.${key}.title`),
      message: t(`organization.features.sso.modals.${key}.message`, {
        returnObjects: true,
      }) as string[],
      onConfirm: () => {
        idp.updateOrgIdpConnection(connection.id, enabled)
        onClose()
        return void 0
      },
    })
    onOpen()
  }

  const stateColumnWidth = '164px'

  return (
    <>
      <Table.Root variant="outline" size="md">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              {t('organization.features.sso.table.headers.name')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('organization.features.sso.table.headers.domains')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('organization.features.sso.table.headers.strategy')}
            </Table.ColumnHeader>
            <Table.ColumnHeader width={stateColumnWidth}>
              {t('organization.features.sso.table.headers.state')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {idpConnections.isLoading &&
            Array.from({ length: 2 }, (_, rowIndex) => (
              <Table.Row
                key={`skeleton-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: for the loading animation, it's ok to use the row-index as a key
                  rowIndex
                }`}
              >
                {Array.from({ length: 4 }, (_, columnIndex) => (
                  <Table.Cell
                    key={`cell-${rowIndex}-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: for the loading animation, it's ok to use the column-index as a key
                      columnIndex
                    }`}
                  >
                    <Skeleton h="20px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          {idpConnections.isError && (
            <Table.Row key="idp-connection-error">
              <Table.Cell colSpan={5} textAlign="center">
                <Text color="text.text-error" fontSize="md">
                  {t('organization.features.sso.table.idpError')}
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
          {noIdpConnection && (
            <Table.Row key="no-idp-connection">
              <Table.Cell colSpan={5} textAlign="center">
                <VStack gap={4} alignItems="center" py={4} mb={0}>
                  <Text fontSize="md" fontWeight="bold" color="gray.500" mb={0}>
                    {t('organization.features.sso.table.noConnections')}
                  </Text>
                </VStack>
              </Table.Cell>
            </Table.Row>
          )}
          {showIdpConnectionList &&
            idpConnections.data.items.map((connection, index) => (
              <Table.Row key={`idp-connection-${connection.id}-${index}`}>
                <Table.Cell>
                  {connection.display_name ||
                    t('organization.features.sso.table.unnamed')}
                </Table.Cell>
                <Table.Cell>
                  {connection.domain_aliases.length > 0 ? (
                    connection.domain_aliases.join(', ')
                  ) : (
                    <Text color="gray.500">
                      {t('organization.features.sso.table.noDomains')}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {strategyDisplayNameMap[connection.strategy] ||
                    t('organization.features.sso.table.unknownStrategy')}
                </Table.Cell>
                <Table.Cell
                  display="flex"
                  justifyContent="space-between"
                  width={stateColumnWidth}
                >
                  <Tag.Root
                    size="sm"
                    colorPalette={connection.enabled ? 'green' : 'gray'}
                  >
                    <Tag.Label>
                      {connection.enabled
                        ? t('organization.features.status.active')
                        : t('organization.features.status.inactive')}
                    </Tag.Label>
                  </Tag.Root>
                  <Switch.Root
                    id={`idp-connection-enabled-${connection.id}`}
                    colorPalette="green"
                    checked={!!connection.enabled}
                    onCheckedChange={(details) => {
                      const newEnabled = details.checked
                      onToggleConnection(connection, newEnabled)
                    }}
                  >
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.HiddenInput />
                  </Switch.Root>
                </Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table.Root>

      <Dialog.Root
        open={open}
        onOpenChange={(details) => !details.open && onClose()}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{modalContent.title}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="start" gap={3}>
                {modalContent.message.map((msg) => (
                  <Text fontSize="md" key={msg}>
                    {msg}
                  </Text>
                ))}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={onClose} mr={3}>
                {t('organization.features.actions.cancel')}
              </Button>
              <Button colorPalette="red" onClick={modalContent.onConfirm}>
                {t('organization.features.actions.confirm')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  )
}
